import React from 'react';
import { Card, Empty, Typography } from 'antd';
import { useSupplierMapping } from './hooks/useSupplierMapping';
import MappingHeader from './components/MappingHeader';
import MappingTable from './components/MappingTable';
import MapVendorModal from './components/MapVendorModal';
import CreateVendorModal from './components/CreateVendorModal';

/**
 * Supplier Mapping page component — orchestrates the feature without owning any logic.
 *
 * All state and side-effects are delegated to `useSupplierMapping`. This component
 * is responsible only for wiring the hook's return value to the four child components:
 * `MappingHeader`, `MappingTable`, `MapVendorModal`, and `CreateVendorModal`.
 *
 * Routing: mounted at `/site-administration/supplier_mapping` via `ClientAdminCard`.
 * @returns {React.ReactElement} The full Supplier Mapping feature page.
 */
const SupplierMapping: React.FC = () => {
  const {
    // Client search
    loading,
    results,
    selectedClient,
    handleSearch,
    handleSelect,
    handleClear,

    // Supplier table
    suppliersLoading,
    displayedSuppliers,
    handleRemoveClientSupplier,
    handleOpenMapVendorModal,
    showUnmappedOnly,
    handleToggleUnmappedFilter,
    handleRefreshSuppliers,
    isBulkMatching,
    bulkMatchSummary,
    bulkReviewQueue,
    bulkReviewIndex,
    handleBulkMatch,
    handleBulkReviewSkip,
    handleDismissBulkSummary,

    // Map vendor modal
    isMapModalOpen,
    activeSupplier,
    mapModalSessionId,
    mapSearchRef,
    vendorSearchFn,
    defaultVendorOptions,
    handleCloseMapVendorModal,
    handleSelectVendor,
    handleOpenCreateVendorModal,
    simKeySearchLoading,
    simKeyResults,
    handleConfirmSimKeyMatch,

    // Create vendor modal
    isCreateVendorModalOpen,
    isSubmitting,
    createVendorForm,
    modalVendorTarget,
    handleCancelCreateVendor,
    handleCreateAndMapVendor,
    dedupCandidates,
    handleConfirmDedupCreate,
    handleUseDedupMatch
  } = useSupplierMapping();

  const queueProgress = bulkReviewQueue.length > 0 ? `${bulkReviewIndex + 1} of ${bulkReviewQueue.length}` : undefined;

  return (
    <>
      <Typography.Title level={4}>Supplier Mapping</Typography.Title>

      <MappingHeader
        loading={loading}
        results={results}
        selectedClient={selectedClient}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onClear={handleClear}
      />

      {!selectedClient && (
        <Card className="mt-4">
          <Empty description="Select a client above to begin the supplier mapping process." />
        </Card>
      )}

      {selectedClient && (
        <MappingTable
          suppliers={displayedSuppliers}
          loading={suppliersLoading}
          onMapVendor={handleOpenMapVendorModal}
          onRemoveSupplier={handleRemoveClientSupplier}
          showUnmappedOnly={showUnmappedOnly}
          onToggleUnmappedFilter={handleToggleUnmappedFilter}
          onRefresh={handleRefreshSuppliers}
          onBulkMatch={handleBulkMatch}
          isBulkMatching={isBulkMatching}
          bulkMatchSummary={bulkMatchSummary}
          onDismissBulkSummary={handleDismissBulkSummary}
        />
      )}

      <MapVendorModal
        open={isMapModalOpen}
        activeSupplier={activeSupplier}
        sessionId={mapModalSessionId}
        searchRef={mapSearchRef}
        defaultOptions={defaultVendorOptions}
        onClose={handleCloseMapVendorModal}
        onSelectVendor={handleSelectVendor}
        onCreateNew={handleOpenCreateVendorModal}
        searchFn={vendorSearchFn}
        simKeySearchLoading={simKeySearchLoading}
        simKeyResults={simKeyResults}
        onConfirmSimKeyMatch={handleConfirmSimKeyMatch}
        queueProgress={queueProgress}
        onSkip={bulkReviewQueue.length > 0 ? handleBulkReviewSkip : undefined}
      />

      <CreateVendorModal
        open={isCreateVendorModalOpen}
        isSubmitting={isSubmitting}
        vendorTarget={modalVendorTarget}
        form={createVendorForm}
        onCancel={handleCancelCreateVendor}
        onFinish={handleCreateAndMapVendor}
        dedupCandidates={dedupCandidates}
        onConfirmDedupCreate={handleConfirmDedupCreate}
        onUseDedupMatch={handleUseDedupMatch}
      />
    </>
  );
};

export default SupplierMapping;
